/**
 * VideoNodeView — 视频节点内容区域
 *
 * 功能：
 *   ① 视频播放控制（播放/暂停/进度条拖拽/时间显示）
 *   ② 全屏预览（双击或全屏按钮）
 *   ③ ✂️ 快剪按钮 → VideoClipModal 弹窗 → 异步 FFmpeg.wasm 导出新视频节点
 *   ④ 🎵 音频分离按钮 → 浏览器端 FFmpeg.wasm stream copy → 弹出新音频节点
 *   ⑤ 截帧提取（首帧/当前帧/末帧 → 弹出图片节点）
 *   ⑥ ☁️ 上传中指示器 / 分辨率标签 / 音频轨道检测
 *
 * 工具栏布局（选中节点时底部显示）：
 *   [✂️快剪] [🎵音频分离] | [首帧][当前帧][末帧] | [下载][更换][全屏] [控制台]
 *
 * 音频分离流程（浏览器端处理，零上传）：
 *   ① 暂停播放 → ② 创建 loading 音频节点 → ③ 加载 FFmpeg.wasm
 *   → ④ FFmpeg -vn -acodec copy（stream copy, 不重编码, 极快）
 *   → ⑤ 本地 blob 预览 → ⑥ 后台上传到服务器/OSS
 *
 * 快剪流程（浏览器端处理）：
 *   ① 弹窗选区间 → ② 创建 loading 视频节点 → ③ FFmpeg.wasm 裁剪
 *   → ④ 本地 blob 预览 → ⑤ 后台上传 OSS → ⑥ 替换为云端 URL
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, Maximize, Download, Film, Volume2, VolumeX, RefreshCw, Minimize2, ChevronDown, Scissors, CloudUpload, AudioLines, Loader2 } from 'lucide-react';
import { NodeResolutionTag, IconFirstFrame, IconMiddleFrame, IconLastFrame, downloadFile, buildDownloadFilename, ToolBtn, formatBytes, useFileBytes } from './NodeCommons';
import FullscreenModal from '../FullscreenModal';
import QuickTooltip from '../components/QuickTooltip';
import { extractFrameAtTime } from '../videoUtils';
import VideoClipModal from '../components/VideoClipModal';
import localCacheService from '../services/localCacheService';
import api from '../api';
import { clipVideo, extractAudioFromVideo, loadFFmpeg, isFFmpegSupported } from '../services/ffmpegService';
import { ossVideoPoster } from '../utils/imageThumb';
import { MIN_NODE_WIDTH } from '../editorUtils';

// 已废弃的存储域名（R2 迁 OSS 后遗留），这些 URL 域名解析永远失败，不要渲染到 <video src>
const DEAD_STORAGE_HOSTS = new Set(['r2-shucai.cineart.top', 'r2-ref.cineart.top']);
function isDeadUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try { return DEAD_STORAGE_HOSTS.has(new URL(url).host); } catch (_) { return false; }
}

// P1: React.memo 防止父级 canvas 重渲染时级联到不相关的节点视图
export default React.memo(function VideoNodeView({ id, data, selected, isSingleSelect = false, xPos, yPos, zoom = 1, showConsole, onToggleConsole }) {
  const videoRef = useRef(null);
  // 🔧 记录上一次的 output，用于检测是否换了新视频（与 ImageNodeView 的 prevOutputRef 对称）
  const prevOutputRef = useRef(data.output);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [vidRes, setVidRes] = useState(() => (data.width && data.height) ? { w: data.width, h: data.height } : null);
  // 快剪弹窗
  const [showClipModal, setShowClipModal] = useState(false);
  // 音频分离中
  const [extractingAudio, setExtractingAudio] = useState(false);
  
  // 优先使用本地缓存 URL（local-cache:// 不可直接用于 <video src>）
  // isDeadUrl: R2 迁移后遗留的死链，不渲染避免浏览器反复发请求刷屏
  const displayUrl = data.localCacheUrl || (data.output && !data.output.startsWith('local-cache://') && !isDeadUrl(data.output) ? data.output : null);
  // 🎬 首帧海报：优先用生成时存好的 data.posterUrl（自建化，独立文件、不依赖 OSS）；
  //   没有则回退 OSS video/snapshot 现截（过渡期兼容旧视频）。卡片默认显示这张首帧图，
  //   像视频已下好一样；点选/播放才真正加载 <video>。
  const cloudVideoUrl = (data.output && /^https?:\/\//i.test(data.output) && !isDeadUrl(data.output)) ? data.output : null;
  const posterUrl = data.posterUrl || ossVideoPoster(cloudVideoUrl);
  // 是否正在从本地缓存解析 blob URL
  const isResolvingCache = !!(data.output?.startsWith('local-cache://') && !data.localCacheUrl);
  // 视频文件体积（HEAD 取 Content-Length，不下载正文）
  const videoBytes = useFileBytes(displayUrl);
  
  // --- Loading 逻辑优化：最少展示 2 秒 ---
  const [resourceLoaded, setResourceLoaded] = useState(false);
  const [minTimePassed, setMinTimePassed] = useState(false);

  useEffect(() => {
    // 只有当 data.useLoadingEffect 为 true (来自项目库) 时，才强制等待
    if (data.useLoadingEffect) {
        const timer = setTimeout(() => {
            setMinTimePassed(true);
        }, 500); 
        return () => clearTimeout(timer);
    } else {
        // 本地上传直接通过
        setMinTimePassed(true);
    }
  }, [data.useLoadingEffect]);

  // ⚠️ 视频加载状态基于真实���件，不要改回短时间兜底！
  //
  // 历史 bug：原来 cloud URL 兜底 3s 就设 resourceLoaded=true，
  //   导致视频首帧还没解码完就移除 shimmer，用户看到黑屏。
  //
  // 修复方案：
  //   - blob URL: 2s 兜底（本地解析通常 <100ms）
  //   - cloud URL: 30s 兜底（仅作最后防线，正常由 onLoadedData 事件触发）
  //   - <video> 上使用 onLoadedData（首帧解码完成）而非 onLoadedMetadata
  //     来设置 resourceLoaded=true
  useEffect(() => {
    if (!displayUrl) return;
    setResourceLoaded(false);
    const isBlobOrData = displayUrl.startsWith('blob:') || displayUrl.startsWith('data:');
    const fallbackMs = isBlobOrData ? 2000 : 30000;
    const fallback = setTimeout(() => setResourceLoaded(true), fallbackMs);
    return () => clearTimeout(fallback);
  }, [displayUrl]);

  const isFinallyLoaded = resourceLoaded && minTimePassed;

  const [hasAudioTrack, setHasAudioTrack] = useState(null);

  const hasContent = !!displayUrl;

  // Plan A 专用：如果已有尺寸 (size已绑定到 data.customSize)，则视频应该 contain 而不是 cover
  const fitMode = data.customSize ? 'contain' : 'cover';

  // 如果有内容但尚未加载完 / 正在解析本地缓存，显示 shimmer
  // 注意：data.uploading 不再阻塞显示——上传期间用户可立即编辑本地缓存的视频，
  // 上传状态通过标题 "上传中..." + 右上角云朵图标提示
  const isLoading = isResolvingCache || (hasContent && !isFinallyLoaded);

  // 真实下载进度：从 localCacheService 的 downloadProgressStore 轮询
  // 下载由 EditorPage → getLocalUrlOrFetch() 触发，进度存储在 localCacheService 模块级 Map 中
  // 此处 500ms 间隔读取，仅触发当前节点重渲染，不影响其他节点
  const [downloadInfo, setDownloadInfo] = useState(null);
  useEffect(() => {
    if (!isLoading || isResolvingCache || !data.output) { setDownloadInfo(null); return; }
    const poll = () => setDownloadInfo(localCacheService.getDownloadProgressByUrl(data.output));
    poll();
    const timer = setInterval(poll, 500);
    return () => clearInterval(timer);
  }, [isLoading, isResolvingCache, data.output]);

  // 🆕 视频缓冲进度（利用 <video> progress 事件）
  const [bufferProgress, setBufferProgress] = useState(0);
  const handleBufferProgress = useCallback(() => {
    const v = videoRef.current;
    if (!v?.duration || !v.buffered.length) return;
    const pct = Math.round((v.buffered.end(v.buffered.length - 1) / v.duration) * 100);
    setBufferProgress(pct);
  }, []);

  // 切换到新视频时重置缓冲进度，避免旧进度残留造成误导。
  useEffect(() => {
    setBufferProgress(0);
  }, [displayUrl]);

  const downloadPercent = downloadInfo && downloadInfo.total > 0
    ? Math.round((downloadInfo.downloaded / downloadInfo.total) * 100)
    : null;
  const isDownloading = !!(downloadInfo && downloadInfo.status === 'downloading');
  const isDownloadDone = !!(downloadInfo && downloadInfo.status === 'done');
  const isWaitingFirstFrame = !resourceLoaded && !isResolvingCache && hasContent;
  // 「真正在加载」= 解析缓存中 / 正在下载 / 已单选且在等首帧。
  //   未选中的视频不算——它 preload="metadata" 本就不主动解首帧，不该转圈假装在加载。
  const isActivelyLoading = isResolvingCache || isDownloading || (isSingleSelect && isWaitingFirstFrame);

  let loadingStageText = '阶段 1/3 读取缓存';
  let loadingDetailText = '正在读取缓存...';
  if (!isResolvingCache) {
    if (isDownloading) {
      loadingStageText = '阶段 2/3 下载资源';
      loadingDetailText = downloadPercent !== null
        ? `正在下载视频 ${downloadPercent}% (${formatBytes(downloadInfo.downloaded)}/${formatBytes(downloadInfo.total)})`
        : `正在下载视频... ${formatBytes(downloadInfo.downloaded)}`;
    } else if (isWaitingFirstFrame) {
      if (bufferProgress > 0 && bufferProgress < 100) {
        loadingStageText = '阶段 3/3 视频缓冲';
        loadingDetailText = `正在缓冲视频 ${bufferProgress}%`;
      } else if (isDownloadDone || bufferProgress >= 100) {
        loadingStageText = '阶段 3/3 首帧解码';
        loadingDetailText = '下载完成，正在解码首帧...';
      } else {
        loadingStageText = '阶段 3/3 准备播放';
        loadingDetailText = '正在初始化视频解码器...';
      }
    } else {
      loadingStageText = '阶段 3/3 准备播放';
      loadingDetailText = '正在加载视频...';
    }
  }

  const togglePlay = (e) => {
    e.stopPropagation(); 
    const video = videoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        // 点击播放时，强制解除静音并拉满音量
        video.muted = false;
        video.volume = 1.0;
        video.play().catch(err => console.error("播放失败:", err));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const curr = videoRef.current.currentTime;
      const dur = videoRef.current.duration || 1;
      setCurrentTime(curr);
      setProgress((curr / dur) * 100);
    }
  };

  const handleLoadedMetadata = (e) => {
    const v = e.target;
    setDuration(v.duration);
    setVidRes({ w: v.videoWidth, h: v.videoHeight });

    // 【修正】：视频节点自适应逻辑
    // 只在节点没有 customSize 时才自动计算初始尺寸
    // 如果已有 customSize（用户手动调整过或从数据库加载），保持不变
    
    // 🔧 检测是否换了新视频：只有新 output 到达时才强制重算尺寸
    // 如果 output 未变（用户手动拖拽调整过 customSize），则保留用户的调整
    const outputChanged = data.output !== prevOutputRef.current;
    if (outputChanged) prevOutputRef.current = data.output;

    // 🔧 比例不匹配检测：覆盖"离开房间后回来"的场景
    // 此时 DB 有旧 customSize + 新 output，prevOutputRef 初始化为当前 output（看不出是新视频），
    // 但 customSize 的比例和实际视频比例不一致 → 强制重算
    let ratioMismatch = false;
    if (data.customSize && data.customSize.width > 0 && data.customSize.height > 0 && v.videoWidth && v.videoHeight) {
        const nodeRatio = data.customSize.width / data.customSize.height;
        const mediaRatio = v.videoWidth / v.videoHeight;
        ratioMismatch = Math.abs(nodeRatio - mediaRatio) / mediaRatio > 0.05;
    }

    if (data.customSize && data.customSize.width > 0 && data.customSize.height > 0 && !outputChanged && !ratioMismatch) {
        // 已有尺寸、不是新视频、比例匹配 → 跳过自动计算（保留用户手动调整的大小）
    } else if (v.videoWidth && v.videoHeight && data.onUpdate) {
        // 判断是素材节点还是生成节点（从模型节点剥离的不算素材节点）
        const isMaterialNode = (data.isDirectUpload || data.assetId || data.isDetached) && !data.fromModelNode;
        const SHORT_SIDE = isMaterialNode ? 120 : 240;
        let newW, newH;

        if (v.videoWidth >= v.videoHeight) {
            // 横屏或正方形：高度固定
            newH = SHORT_SIDE;
            newW = v.videoWidth * (SHORT_SIDE / v.videoHeight);
        } else {
            // 竖屏：宽度固定
            newW = SHORT_SIDE;
            newH = v.videoHeight * (SHORT_SIDE / v.videoWidth);
        }

        // 🔧 横向宽度下限：竖屏视频宽度过小会让顶部信息条溢出、盖住关闭按钮。
        //    强制保证最小宽度并按比例重算高度，与 calculateLongSide 一致。
        if (newW < MIN_NODE_WIDTH) {
            newH = newH * (MIN_NODE_WIDTH / newW);
            newW = MIN_NODE_WIDTH;
        }

        setTimeout(() => {
            data.onUpdate?.(id, { customSize: { width: Math.round(newW), height: Math.round(newH) } });
        }, 50);
    }

    // 听诊逻辑
    let audioDetected = false;
    if (v.mozHasAudio !== undefined) {
      audioDetected = v.mozHasAudio;
    } else if (v.audioTracks && v.audioTracks.length > 0) {
      audioDetected = true;
    } else if (v.captureStream) {
      try {
        const stream = v.captureStream();
        const tracks = stream.getAudioTracks();
        if (tracks && tracks.length > 0) audioDetected = true;
        // 🛡️ 立即释放所有轨道，防止 MediaStream 泄漏
        stream.getTracks().forEach(t => t.stop());
      } catch (err) {
        console.warn("Audio detection failed:", err);
      }
    } else if (v.webkitAudioDecodedByteCount > 0) {
      audioDetected = true;
    }
    setHasAudioTrack(audioDetected);
  };

  const handleSeek = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newTime = (x / rect.width) * videoRef.current.duration;
    
    videoRef.current.muted = false;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
    setProgress((x / rect.width) * 100);
  };

  const handleExtractTime = async (e, type) => {
    e.stopPropagation();
    if (!hasContent) return;
    try {
      const dataUrl = await extractFrameAtTime(displayUrl, type);
      // ✅ 将 data: URL 转为 blob: URL，确保 handleDetachImage 能触发自动上传
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      data.onDetach?.(id, blobUrl, { x: xPos, y: yPos }, 'image');
    } catch (err) {
      console.error("截帧失败:", err);
    }
  };

  const handleExtractCurrent = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
    // ✅ 使用 toBlob 生成 blob: URL（与 ImageNodeView 一致），确保能被自动上传
    canvas.toBlob((blob) => {
      if (blob && data.onDetach) {
        const blobUrl = URL.createObjectURL(blob);
        data.onDetach(id, blobUrl, { x: xPos, y: yPos }, 'image');
      }
    }, 'image/png');
  };

  const formatTime = (seconds) => {
    if (!seconds) return "00:00";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const capsuleStyle = {
    display: 'flex', alignItems: 'center', gap: '4px',
    background: 'var(--toolbar-bg)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '50px', padding: '6px 16px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
    pointerEvents: 'auto', width: 'max-content',
    userSelect: 'none',
    backdropFilter: 'blur(8px)'
  };

  const dividerStyle = { width: '1px', height: '14px', background: 'var(--border-strong)', margin: '0 4px' };

  /* ── 音频分离（浏览器端 FFmpeg.wasm，零上传）─────────────────────────────
   *
   * 完整流程：
   *   ① 暂停视频，创建 loading 音频节点
   *   ② 加载 FFmpeg.wasm（首次约 31MB，浏览器缓存后秒开）
   *   ③ FFmpeg -vn -acodec copy 提取音频流（零重编码，极快）
   *   ④ 本地 blob 预览（立即可播放）
   *   ⑤ 后台上传音频到服务器/OSS
   */
  const handleExtractAudio = useCallback(async () => {
    if (!hasContent || extractingAudio) return;
    if (hasAudioTrack === false) {
      alert('该视频不包含音频轨道，无法提取');
      return;
    }
    if (!isFFmpegSupported()) {
      alert('当前浏览器不支持音频分离，请使用 Chrome / Edge 最新版本');
      return;
    }

    // ① 暂停视频播放
    if (videoRef.current) { videoRef.current.pause(); setIsPlaying(false); }
    setExtractingAudio(true);

    // ② 创建 loading 音频节点
    const newNodeId = await data.onDetach?.(id, null, { x: xPos, y: yPos }, 'audio', {
      clipping: true,
    });
    if (!newNodeId) { setExtractingAudio(false); return; }

    try {
      data.onUpdate?.(newNodeId, { statusInfo: { status: '加载音频引擎...' }, generationModelName: '🎵 音频分离' });
      await loadFFmpeg();

      data.onUpdate?.(newNodeId, { statusInfo: { status: '正在分离音频...' }, generationModelName: '🎵 音频分离' });
      const audioBlob = await extractAudioFromVideo(displayUrl);

      // ④ 本地 blob 预览（立即可播放）
      const localBlobUrl = URL.createObjectURL(audioBlob);
      data.onUpdate?.(newNodeId, {
        output: localBlobUrl, outputs: [localBlobUrl],
        loading: false, clipping: false,
        uploading: true,
        statusInfo: null, generationStartTime: null, generationModelName: null,
        title: (data.title || '视频') + ' - 音频',
      });

      // ⑤ 后台上传到服务器（用户已可预览）
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', audioBlob, `audio-${Date.now()}.m4a`);
        const uploadData = await api.post('/api/upload', uploadForm);
        if (uploadData.url) {
          data.onUpdate?.(newNodeId, {
            output: uploadData.url, outputs: [uploadData.url],
            uploading: false, localCacheUrl: localBlobUrl,
          });
        } else {
          data.onUpdate?.(newNodeId, { uploading: false });
        }
      } catch (uploadErr) {
        console.error('[音频分离] 上传失败:', uploadErr.message);
        data.onUpdate?.(newNodeId, { uploading: false });
      }
    } catch (err) {
      console.error('[音频分离] 失败:', err.message);
      data.onUpdate?.(newNodeId, {
        loading: false, clipping: false,
        error: err.message || '音频分离失败',
        statusInfo: null, generationStartTime: null, generationModelName: null,
      });
    } finally {
      setExtractingAudio(false);
    }
  }, [id, displayUrl, xPos, yPos, data, hasContent, extractingAudio, hasAudioTrack]);

  /* ── 异步快剪导出（非阻塞）──────────────────────────────────────────────
   *
   * 完整流程：
   *   ① 关闭弹窗
   *   ② 创建 loading 节点（封面缩略图 + 原始比例 + Loader2 动画）
   *   ③ 后台 FFmpeg.wasm 裁剪（不支持的浏览器直接报错提示）
   *   ④ 裁剪完成 → 本地 blob 预览（立即可播放）
   *   ⑤ 后台异步上传 OSS（右下角 ☁️ 图标提示）
   *   ⑥ 上传完成 → 替换为云端 URL
   *
   * @param {number}  clipInPoint  - 裁剪入点（秒）
   * @param {number}  clipOutPoint - 裁剪出点（秒）
   * @param {Object}  clipMeta     - 弹窗传来的元数据
   * @param {string}  clipMeta.thumbnail   - 裁剪起始帧截图（dataURL，用于节点封面）
   * @param {number}  clipMeta.videoWidth  - 原始视频宽度（用于节点比例）
   * @param {number}  clipMeta.videoHeight - 原始视频高度
   */
  const handleAsyncClipExport = useCallback(async (clipInPoint, clipOutPoint, clipMeta = {}) => {
    setShowClipModal(false);                                      // ① 关闭弹窗

    const { thumbnail, videoWidth, videoHeight } = clipMeta;

    // ② 创建 loading 节点：封面 + 原始比例 + 裁剪动画
    const newNodeId = await data.onDetach?.(id, null, { x: xPos, y: yPos }, 'video', {
      clipping: true, thumbnail, videoWidth, videoHeight,
    });
    if (!newNodeId) return;

    // 🛡️ 裁剪期间拦截页面关闭/刷新（beforeunload）
    const beforeUnloadHandler = (e) => {
      e.preventDefault();
      e.returnValue = '视频正在裁剪中，离开将导致裁剪失败。确定离开吗？';
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);

    try {
      // ── 浏览器端 FFmpeg.wasm 裁剪（使用用户本地算力，帧精确 libx264 重编码）──
      if (!isFFmpegSupported()) {
        throw new Error('当前浏览器不支持视频裁剪，请使用 Chrome / Edge 最新版本');
      }

      data.onUpdate?.(newNodeId, { statusInfo: { status: '加载裁剪引擎...' } });
      await loadFFmpeg();

      // ③ 带进度回调的裁剪（FFmpeg.wasm progress 事件 → 实时更新节点状态）
      const clippedBlob = await clipVideo(displayUrl, clipInPoint, clipOutPoint, (pct) => {
        data.onUpdate?.(newNodeId, { statusInfo: { status: `裁剪中 ${pct}%` } });
      });

      // ④ 裁剪完成 → 本地 blob 预览（立即可播放）
      const localBlobUrl = URL.createObjectURL(clippedBlob);
      data.onUpdate?.(newNodeId, {
        output: localBlobUrl, outputs: [localBlobUrl],
        loading: false, clipping: false, clippingThumbnail: null,
        uploading: true,
        statusInfo: null, generationStartTime: null, generationModelName: null,
      });

      // ⑤ 后台上传 OSS（用户已可预览，右下角显示 ☁️ 上传图标）
      try {
        const uploadForm = new FormData();
        uploadForm.append('file', clippedBlob, `clip-${Date.now()}.mp4`);
        const uploadData = await api.post('/api/upload', uploadForm);
        if (!uploadData.url) throw new Error('服务器未返回视频 URL');
        data.onUpdate?.(newNodeId, {
          output: uploadData.url, outputs: [uploadData.url],
          uploading: false, localCacheUrl: localBlobUrl,
        });
      } catch (uploadErr) {
        console.error('快剪上传失败:', uploadErr);
        data.onUpdate?.(newNodeId, { uploading: false, title: '上传失败（本地可预览）' });
      }
    } catch (err) {
      // 裁剪失败 → 错误状态
      data.onUpdate?.(newNodeId, {
        loading: false, clipping: false, clippingThumbnail: null,
        error: err.message || '裁剪失败',
        statusInfo: null, generationStartTime: null, generationModelName: null,
      });
    } finally {
      // 🛡️ 裁剪结束（成功或失败），移除离开拦截
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    }
  }, [id, displayUrl, xPos, yPos, data]);

  return (
    <>
      {/* ✂️ 快剪弹窗 */}
      {showClipModal && (
        <VideoClipModal
          previewUrl={displayUrl}
          sourceUrl={data.output && !data.output.startsWith('local-cache://') && !data.output.startsWith('blob:') ? data.output : null}
          onClose={() => setShowClipModal(false)}
          onExportAsync={handleAsyncClipExport}
        />
      )}

      {isFullscreen && <FullscreenModal url={displayUrl} fallbackUrl={data.output && !data.output.startsWith('local-cache://') && !data.output.startsWith('blob:') ? data.output : undefined} type="video" generationTime={data.generationTime} onClose={() => setIsFullscreen(false)} />}

      <div style={{ width: '100%', height: '100%', position: 'relative', background: hasContent ? '#000' : 'transparent', borderRadius: '0px', overflow: 'hidden' }}>
        
        {/* Shimmer 占位层：用 node.data.thumbUrl（服务端 400px webp 缩略图）做底图。
            ⚠️ 视频节点的 thumbUrl 当前几乎不会有——sharp 不处理视频，
            generateThumb 跳过了视频类型。视频走纯深灰 shimmer + 进度条。
            保留这段代码是为了将来支持视频首帧缩略图。 */}
        {isLoading && (
            <div className="shimmer-wrapper" style={{
                width: '100%', height: '100%',
                position: 'absolute', top: 0, left: 0, zIndex: 1
            }}>
              {/* 首帧海报（OSS video/snapshot 截帧）作底图：卡片即显首帧画面，像视频已下好一样。
                  没有 OSS 截帧时退回 thumbUrl 模糊图；再没有就纯深灰 shimmer。 */}
              {posterUrl ? (
                <img src={posterUrl} alt="" aria-hidden
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: fitMode, pointerEvents: 'none', zIndex: 0 }} />
              ) : data.thumbUrl ? (
                <img src={data.thumbUrl} alt="" aria-hidden
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(2px)', pointerEvents: 'none', zIndex: 0 }} />
              ) : null}
              {/* 只有「真正在加载」(解析缓存 / 下载中 / 已选中缓冲) 才叠转圈 + 阶段文案；
                  未选中的空闲态只显示首帧海报，不转圈、不放播放按钮。 */}
              {isActivelyLoading && (
                <div style={{ position:'absolute', inset:0, zIndex:2, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none', background: posterUrl ? 'rgba(0,0,0,0.35)' : 'transparent' }}>
                  <Loader2 size={16} strokeWidth={2} color="rgba(255,255,255,0.7)" style={{ animation:'spin 1s linear infinite' }} />
                  <span style={{ marginTop:4, fontSize:9, color:'rgba(255,255,255,0.72)', letterSpacing:'0.3px', fontWeight:600 }}>
                    {loadingStageText}
                  </span>
                  <span style={{ marginTop:2, fontSize:9, color:'rgba(255,255,255,0.5)', letterSpacing:'0.2px', fontWeight:500 }}>
                    {loadingDetailText}
                  </span>
                </div>
              )}
            </div>
        )}

        {/* ☁️ 上传中指示器：视频可编辑，右下角小图标提示后台上传 */}
        {data.uploading && !isLoading && (
            <div style={{
                position: 'absolute', bottom: 6, right: 6, zIndex: 25,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.55)', borderRadius: '50%',
                width: 24, height: 24,
                color: '#93c5fd', pointerEvents: 'none',
                backdropFilter: 'blur(4px)',
            }}>
                <CloudUpload size={13} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
            </div>
        )}

        {hasContent ? (
          <>
            <video
              ref={videoRef}
              src={displayUrl}
              // 🎬 预览优先：始终只 preload="metadata"（下视频头 moov+首帧≈封面，几乎不耗流量），
              //    只有用户播放/全屏时才按需加载完整视频，避免低速网络下后台下载吃满带宽。
              //    —— 教育网/热点场景：单选节点不再自动下载整段视频（10-100MB），只下载元数据(<1MB)；
              //    点击播放/双击全屏时才实时加载，有明确的进度反馈。
              preload="metadata"
              poster={posterUrl || data.thumbUrl || undefined}
              style={{
                  width: '100%', height: '100%',
                  objectFit: fitMode, // 动态适配模式
                  background: fitMode === 'contain' ? '#000' : 'transparent', // 留白填充黑底
                  // 🎨 fade-in：首帧解码前 opacity=0，避免视频元素自身浅色背景闪现
                  // shimmer-wrapper 的深灰背景一直撑到 onLoadedData
                  opacity: isFinallyLoaded ? 1 : 0,
                  transition: 'opacity 220ms ease-out',
                  cursor: 'pointer'
              }}
              // 🛡️ blob URL 失效时回退到云存储 URL（重启电脑后旧 blob 不可用）
              // isDeadUrl: 死链不回退，避免触发无限重试
              onError={(e) => {
                  if (data.output && e.target.src !== data.output && !data.output.startsWith('local-cache://') && !isDeadUrl(data.output)) {
                      e.target.src = data.output;
                  }
              }}
              // 【新增】双击全屏
              onDoubleClick={(e) => {
                  e.stopPropagation();
                  // 【修复】进入全屏前，先暂停图板上的视频播放
                  if (videoRef.current && isPlaying) {
                      videoRef.current.pause();
                      setIsPlaying(false);
                  }
                  setIsFullscreen(true);
              }}
              crossOrigin="anonymous"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              // ⚠️ 必须用 onLoadedData 而非 onLoadedMetadata 来解除 shimmer！
              // onLoadedMetadata = 元数据解析完（尺寸/时长），视频可能仍是黑屏
              // onLoadedData = 首帧已解码并可渲染，视频有实际画面
              onLoadedData={(e) => {
                setResourceLoaded(true);
              }}
              onEnded={() => setIsPlaying(false)}
              onProgress={handleBufferProgress}
            />
            
            {/* 【核心修复】：直接使用组件，移除所有包裹 div，彻底解决竖排问题 */}
            <NodeResolutionTag w={vidRes?.w} h={vidRes?.h} generationTime={data.generationTime} fileBytes={videoBytes} />

            {/* 底部控制栏 */}
            <div className="nodrag" style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              padding: '4px 6px',
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
              display: 'flex', flexDirection: 'column', gap: '3px', zIndex: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '8px', color: '#ddd', fontFamily: 'monospace' }}>
                
                {/* 左侧：播放按钮 + 时间 + 音频标识 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <div 
                    onClick={togglePlay} 
                    style={{ 
                      cursor: 'pointer', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: '14px', height: '14px', borderRadius: '50%',
                      background: '#3b82f6', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                  >
                    {isPlaying ? (
                      <Pause size={7} fill="#fff" color="#fff"/> 
                    ) : (
                      <Play size={7} fill="#fff" color="#fff" style={{marginLeft:'0.5px'}}/>
                    )}
                  </div>

                  <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
                  
                  {hasAudioTrack === false && (
                    <div title="无音频轨道" style={{ display: 'flex', alignItems: 'center', marginLeft: '2px', opacity: 1 }}>
                      <VolumeX size={8} color="#ef4444" />
                    </div>
                  )}
                  {hasAudioTrack === true && (
                    <div title="包含音频" style={{ display: 'flex', alignItems: 'center', marginLeft: '2px', opacity: 0.5 }}>
                      <Volume2 size={8} color="#fff" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* 进度条 */}
              <div 
                onClick={handleSeek}
                style={{ 
                  width: '100%', height: '4px', background: 'rgba(255,255,255,0.2)', 
                  borderRadius: '2px', cursor: 'pointer', position: 'relative' 
                }}
              >
                <div style={{ 
                  width: `${progress}%`, height: '100%', background: '#3b82f6', 
                  borderRadius: '2px' 
                }} />
              </div>
            </div>
          </>
        ) : (
          <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100%'}}><Film size={48} color="rgba(255,255,255,0.4)" strokeWidth={2} /></div>
        )}

        {/* 生成失败错误遮罩（与图片节点保持一致） */}
        {data.error && (
          <div style={{
            position: 'absolute',
            top: '0', left: '0', right: '0', bottom: '0',
            background: 'rgba(0,0,0,0.55)',
            zIndex: 1002,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '12px'
          }}>
            <div style={{
              color: '#fff', fontSize: 14, fontWeight: 500, marginBottom: 18,
              textShadow: '0 2px 8px #000', textAlign: 'center',
              maxWidth: '92%', wordBreak: 'break-word'
            }}>{data.error}</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <ToolBtn
                onClick={() => data.onGenerate?.(id)}
                title="重新生成"
                color="#94a3b8"
                size="32px"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s',
                  width: '32px', height: '32px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                }}
              >
                <RefreshCw size={18} color="#94a3b8" strokeWidth={2} />
              </ToolBtn>
              <ToolBtn
                onClick={() => data.onUpdate?.(id, { error: null })}
                title="关闭"
                color="#94a3b8"
                size="32px"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', transition: 'all 0.2s',
                  width: '32px', height: '32px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
                }}
              >
                <Minimize2 size={18} color="#94a3b8" strokeWidth={2} />
              </ToolBtn>
            </div>
          </div>
        )}
      </div>

      {selected && (
        <div className="nodrag" style={{ 
            position: 'absolute', top: '100%', left: '0', right: '0', height: '0', zIndex: 1001 
        }}>
          <div style={{
            position: 'absolute',
            top: '2px', left: '50%',
            transform: `translateX(-50%) scale(${(1 / zoom) * 0.9})`,
            transformOrigin: 'top center',
            ...capsuleStyle
          }}>
            {/* ✂️ 快剪按钮（有视频内容即可使用，本地视频和云存储视频均支持） */}
            <ToolBtn
              disabled={!hasContent}
              onClick={() => {
                // 打开快剪前暂停图板上的视频播放，避免底层视频继续播放
                if (videoRef.current) { videoRef.current.pause(); setIsPlaying(false); }
                setShowClipModal(true);
              }}
              title={!hasContent ? '无视频' : '视频快剪'}
            ><Scissors size={16} strokeWidth={1.5} /></ToolBtn>

            {/* 🎵 音频分离按钮 */}
            <ToolBtn
              disabled={!hasContent || extractingAudio || hasAudioTrack === false}
              onClick={handleExtractAudio}
              title={!hasContent ? '无视频' : hasAudioTrack === false ? '无音频轨道' : extractingAudio ? '分离中...' : '音频分离'}
            >
              <AudioLines size={16} strokeWidth={1.5} />
            </ToolBtn>

            <div style={dividerStyle}></div>

            <ToolBtn disabled={!hasContent} onClick={(e) => handleExtractTime(e, 'first')} title={!hasContent ? "无视频" : "提取首帧"}><IconFirstFrame size={18} /></ToolBtn>
            <ToolBtn disabled={!hasContent} onClick={handleExtractCurrent} title={!hasContent ? "无视频" : "提取当前帧"}><IconMiddleFrame size={18} /></ToolBtn>
            <ToolBtn disabled={!hasContent} onClick={(e) => handleExtractTime(e, 'last')} title={!hasContent ? "无视频" : "提取末帧"}><IconLastFrame size={18} /></ToolBtn>
            
            <div style={dividerStyle}></div>
            <ToolBtn disabled={!hasContent} onClick={() => { downloadFile(data.localCacheUrl || data.output, buildDownloadFilename(data.title, data.output, '.mp4')); }} title={!hasContent ? "无视频" : "下载"}><Download size={18} strokeWidth={1.5} /></ToolBtn>
            <ToolBtn onClick={() => data.onReplace?.(id, 'video')} title="更换素材"><RefreshCw size={16} strokeWidth={1.5} /></ToolBtn>
            <ToolBtn disabled={!hasContent} onClick={() => {
              // 【修复】进入全屏前，先暂停图板上的视频播放
              if (videoRef.current && isPlaying) {
                videoRef.current.pause();
                setIsPlaying(false);
              }
              setIsFullscreen(true);
            }} title="全屏"><Maximize size={18} strokeWidth={1.5} /></ToolBtn>

            {/* 🎛️ 控制台开关按钮 - 只在生成节点（非容器节点）显示 */}
            {!data.isDirectUpload && !data.assetId && (
              <QuickTooltip content={showConsole ? "收起控制台" : "打开控制台"} position="top">
                <div 
                  onClick={onToggleConsole}
                  style={{
                    width: '30px', height: '30px',
                    borderRadius: '50%',
                    background: 'var(--console-toggle-bg)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    border: '1px solid rgba(255,255,255,0.2)',
                    marginLeft: '8px'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--console-toggle-hover)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'var(--console-toggle-bg)';
                  }}
                >
                  <ChevronDown size={18} style={{
                    transform: showConsole ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s',
                    color: 'var(--console-toggle-fg)'
                  }} />
                </div>
              </QuickTooltip>
            )}
          </div>
        </div>
      )}
    </>
  );
});